import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { IDS } from "@/lib/testIds";
import { useAuth } from "@/lib/auth";
import { isAdmin, isUser } from "@/lib/roles";
import { toast } from "sonner";
import ExpandableCard from "@/components/ExpandableCard";
import LiveInventoryContent from "@/components/LiveInventoryContent";
import MonthlyMovementContent from "@/components/MonthlyMovementContent";
import {
  Package as PackageIcon, ShoppingCart, ArrowRight,
  QrCode, Warning, ClipboardText, ChartLine, Bell, ListChecks,
} from "@phosphor-icons/react";

export default function Dashboard() {
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [mealSummary, setMealSummary] = useState(null);
  const [sessionName, setSessionName] = useState("");
  const nav = useNavigate();
  const admin = isAdmin(user);
  const regularUser = isUser(user);

  useEffect(() => {
    api.get("/inventory/summary").then((r) => setSummary(r.data)).catch((e) => toast.error(formatApiError(e)));
    api.get("/meals/summary").then((r) => setMealSummary(r.data)).catch(() => {});
  }, []);

  const createSession = async () => {
    const name = sessionName.trim() || `Count ${new Date().toLocaleString()}`;
    try {
      const { data } = await api.post("/inventory/sessions", { name, count_type: "full" });
      nav(`/sessions/${data.session_id}`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // Card gating per role. USER: only Live Inventory. SUPERVISOR: no Low Stock/Reorder.
  // ADMIN: all four.
  const showMonthly = !regularUser;
  const showLowStock = admin;
  const showAlerts = !regularUser;

  return (
    <div className="p-4 md:p-6 max-w-7xl">
      <div className="flex items-end justify-between mb-6 gap-3 flex-wrap">
        <div>
          <div className="label-tech mb-1">Command Center</div>
          <h1 className="font-display font-black text-3xl md:text-4xl tracking-tight">Dashboard</h1>
        </div>
        <Link to="/scan" data-testid={IDS.scanFab}
          className="btn-hard flex items-center gap-2 h-12 px-5 bg-primary text-primary-foreground font-bold uppercase tracking-widest text-xs rounded-sm hover:bg-orange-600 hover:text-white">
          <QrCode size={20} weight="fill" /> Scan Item
        </Link>
      </div>

      {/* KPI cards — count determined by role. */}
      <div className={`grid grid-cols-1 ${regularUser ? "md:grid-cols-1" : "md:grid-cols-2"} gap-3 mb-6`}>
        <ExpandableCard
          testid="card-live-inventory" title="Live Inventory"
          subtitle={`${mealSummary?.total_records ?? "—"} meal records · ${summary?.total_items ?? "—"} dry-goods variants`}
          summary={`${Math.max(0, mealSummary?.total_meals ?? 0).toFixed(0)} meals on hand · ${summary?.total_locations ?? 0} locations`}
          icon={PackageIcon}
        >
          <LiveInventoryContent />
        </ExpandableCard>
        {showMonthly && (
          <ExpandableCard testid="card-monthly-movement" title="Monthly Inventory Movement"
            subtitle="Meals & Dry Goods activity" summary="Last 30 days · adds / take-outs / packs" icon={ChartLine}>
            <MonthlyMovementContent />
          </ExpandableCard>
        )}
        {showLowStock && (
          <ExpandableCard testid="card-low-stock" title="Low Stock & Reorder"
            subtitle={`${(summary?.below_par ?? 0) + (mealSummary?.below_par ?? 0)} items below PAR`}
            summary={`Est. reorder cost $${(summary?.estimated_reorder_cost ?? 0).toFixed(0)}`} icon={Warning}>
            <div className="space-y-2">
              <Link to="/reorder" className="btn-hard block h-10 leading-10 px-3 bg-primary text-primary-foreground text-center text-xs font-bold uppercase tracking-widest rounded-sm hover:bg-orange-600 hover:text-white">
                Open Reorder Dashboard →
              </Link>
              <div className="text-xs text-muted-foreground">Suggested Quantity = Target Stock − Current Inventory (clamped at 0). Priority levels: Critical · Low · Normal.</div>
            </div>
          </ExpandableCard>
        )}
        {showAlerts && (
          <ExpandableCard testid="card-alerts" title="Alerts & Exceptions"
            subtitle={`${mealSummary?.out_of_stock ?? 0} meal records out of stock`}
            summary="Failed scans · negative overrides · reversals" icon={Bell}>
            <div className="text-xs text-muted-foreground space-y-2">
              <div>Meals out of stock: <span className="font-mono text-destructive">{mealSummary?.out_of_stock ?? 0}</span></div>
              <div>Dry goods out of stock: <span className="font-mono text-destructive">{summary?.out_of_stock ?? 0}</span></div>
              {admin && (
                <Link to="/admin/audit" className="btn-hard block h-9 leading-9 px-3 border border-border text-center text-[10px] font-bold uppercase tracking-widest rounded-sm hover:bg-muted mt-1">
                  View Audit Log →
                </Link>
              )}
            </div>
          </ExpandableCard>
        )}
      </div>

      {/* Diet Type breakdown — visible to all */}
      {mealSummary && (
        <div className="card-flat border-border p-4 mb-6">
          <div className="label-tech mb-3">Meals by Diet Type</div>
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2">
            {mealSummary.by_diet_type.map((d) => (
              <div key={d.diet_type} className="border border-border p-2 rounded-sm">
                <div className="label-tech text-[9px] truncate">{d.diet_type}</div>
                <div className="font-display font-black text-lg">{d.meals.toFixed(0)}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Active session — visible to all */}
      <div className="card-flat border-border p-4 mb-6" data-testid={IDS.summarySession}>
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="label-tech mb-1">Active Session</div>
            <div className="font-display font-black text-xl tracking-tight">
              {summary?.active_session ? summary.active_session.name : "No session in progress"}
            </div>
            {summary?.active_session && (
              <div className="text-xs font-mono text-muted-foreground mt-1">{summary.active_session.session_id} · {summary.active_session.status}</div>
            )}
          </div>
          {summary?.active_session ? (
            <Link to={`/sessions/${summary.active_session.session_id}`}
              className="btn-hard flex items-center gap-1 h-10 px-4 bg-primary text-primary-foreground font-bold uppercase tracking-widest text-xs rounded-sm hover:bg-orange-600 hover:text-white">
              Resume <ArrowRight size={14} weight="bold" />
            </Link>
          ) : null}
        </div>
        {!summary?.active_session && (
          <div className="flex flex-col sm:flex-row gap-2">
            <input data-testid={IDS.sessionName} value={sessionName} onChange={(e) => setSessionName(e.target.value)}
              placeholder="Session name (e.g., Weekly count — Bay 3)"
              className="flex-1 h-11 px-3 border border-border bg-background rounded-sm text-sm outline-none focus:ring-2 focus:ring-primary" />
            <button data-testid={IDS.startSessionBtn} onClick={createSession}
              className="btn-hard h-11 px-4 bg-primary text-primary-foreground font-bold uppercase tracking-widest text-xs rounded-sm hover:bg-orange-600 hover:text-white">
              Start Count Session
            </button>
          </div>
        )}
      </div>

      {/* Bottom quick-action cards — Reorder List only for Admin */}
      <div className={`grid grid-cols-2 ${admin ? "md:grid-cols-4" : "md:grid-cols-3"} gap-3`}>
        <Link to="/scan" data-testid="quick-scan" className="card-flat border-border p-4 hover:border-primary hover:bg-muted/40">
          <QrCode size={22} weight="bold" className="text-primary mb-2" />
          <div className="font-display font-bold">Scan / Search</div>
          <div className="text-xs text-muted-foreground mt-1">Camera & manual entry</div>
        </Link>
        {admin && (
          <Link to="/reorder" data-testid="quick-reorder" className="card-flat border-border p-4 hover:border-primary hover:bg-muted/40">
            <ShoppingCart size={22} weight="bold" className="text-primary mb-2" />
            <div className="font-display font-bold">Reorder List</div>
            <div className="text-xs text-muted-foreground mt-1">PAR-based recommendations</div>
          </Link>
        )}
        <Link to="/sessions" data-testid="quick-sessions" className="card-flat border-border p-4 hover:border-primary hover:bg-muted/40">
          <ClipboardText size={22} weight="bold" className="text-primary mb-2" />
          <div className="font-display font-bold">Count Sessions</div>
          <div className="text-xs text-muted-foreground mt-1">Start & manage counts</div>
        </Link>
        <Link to="/transactions" data-testid="quick-transactions" className="card-flat border-border p-4 hover:border-primary hover:bg-muted/40">
          <ListChecks size={22} weight="bold" className="text-primary mb-2" />
          <div className="font-display font-bold">Inventory Transactions</div>
          <div className="text-xs text-muted-foreground mt-1">Full transaction history</div>
        </Link>
      </div>

      {/* Recent activity — readable rendering */}
      <div className="mt-6 card-flat border-border" data-testid="recent-activity">
        <div className="p-4 border-b border-border flex items-center justify-between">
          <div className="label-tech">Recent Activity</div>
          <div className="text-xs font-mono text-muted-foreground">last 10</div>
        </div>
        {summary?.recent_transactions?.length ? (
          <div className="divide-y divide-border">
            {summary.recent_transactions.map((t) => {
              const label = t.product_name || t.diet_type_and_code || t.variant_id || t.meal_record_id || "—";
              const code = t.product_id || t.diet_code || t.variant_id || "—";
              return (
                <div key={t.transaction_id} className="px-4 py-2.5 flex items-center justify-between text-sm gap-3">
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <span className="label-tech shrink-0">{(t.action || t.kind || "").replace("_", " ")}</span>
                    <div className="min-w-0">
                      <div className="truncate font-medium text-xs">{label}</div>
                      <div className="text-[10px] font-mono text-muted-foreground">{code}</div>
                    </div>
                  </div>
                  <div className="font-mono text-[11px] text-muted-foreground text-right shrink-0">
                    <div>{t.new_qty ?? t.delta ?? ""}</div>
                    <div className="text-[9px]">{new Date(t.timestamp).toLocaleString()}</div>
                    {t.user_email && <div className="text-[9px] truncate max-w-[14ch]">{t.user_email}</div>}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="p-8 text-center text-sm text-muted-foreground">No transactions yet. Start a count session to begin.</div>
        )}
      </div>
    </div>
  );
}
